/* eslint-disable unicorn/no-this-assignment */
/* eslint-disable @typescript-eslint/no-invalid-this */
/* eslint-disable @typescript-eslint/no-this-alias */
import type { RefObject } from 'react';
import type { SankeyTimeline } from './SankeyTimeline';
import type { TimelineLink } from './TimelineLink';
import type { TimelineNode } from './TimelineNode';
import type { TimelineGraph } from './types';
import { Bezier } from 'bezier-js';
import {
  axisBottom,
  type BaseType,
  color,
  drag,
  easeCubicIn,
  type RGBColor,
  type ScaleLinear,
  scaleLinear,
  select,
  selectAll,
  type Selection,
  type Transition,
  transition,
} from 'd3';
import { EventEmitter } from 'ee-ts';
import { colors } from './colors';
import { hasDist } from './util';

/**
 * Renders the chart using D3.
 */
export class Renderer extends EventEmitter<{
  positionChanged: (node: number, x: number, y: number) => void;
}> {
  public options = {
    axis: {
      color: 'rgba(0,0,0,0.25)',
      fontSize: 8,
      height: 2,
      margin: 8,
      tick: {
        height: 12,
        width: 3,
      },
    },
    background: 'var(--emrald-surface)',
    curve: {
      height: 50,
      width: 200,
    },
    distHandle: {
      color: 'rgb(0,0,0)',
      width: 3,
    },
    distributions: false,
    dynamicLinkWidth: true,
    dynamicNodeHeight: false,
    fadeOpacity: 0.3,
    fontColor: 'white',
    fontSize: 20,
    height: window.innerHeight,
    labels: {
      fontSize: 1,
      borderWidth: 6,
    },
    layout: 'default',
    linkTitle: (d: TimelineLink) =>
      `${d.source.label} → ${d.target.label}\n${d.flow.toString()}`,
    margin: 60,
    maxLinkWidth: 50,
    maxNodeHeight: 100,
    meanBar: {
      color: 'rgba(0,0,0,0.25)',
      width: 3,
    },
    nodeTitle: (d: TimelineNode): string => d.label,
    ticks: 10,
    transitionSpeed: 75,
    width: window.innerWidth,
  };

  private graph: TimelineGraph = {
    links: [],
    nodes: [],
  };

  private maxRight = 0;

  private maxY = 0;

  private scale: ScaleLinear<number, number>;

  private container!: Selection<BaseType, HTMLElement, BaseType, any>;

  /**
   * Constructs Renderer.
   * @param timeline - The timeline to render.
   * @param container - The SVG element to render within.
   */
  public constructor(
    private timeline: SankeyTimeline,
    svgRef: RefObject<SVGSVGElement>,
  ) {
    super();
    this.scale = scaleLinear(
      [this.timeline.minTime, this.timeline.maxTime],
      [this.options.margin, this.options.width - this.options.margin],
    );
    this.container = select<BaseType, HTMLElement>(svgRef.current);
  }

  /**
   * Renders the graph.
   */
  public render() {
    this.graph = this.timeline.graph;
    this.container
      .style('background', this.options.background)
      .style('top', '23px')
      .style('position', 'relative')
      .style('margin-bottom', '2px');
    this.calculateLabelSizes();
    this.initializeLayout();
    this.calculateLayout();
    this.calculateLinkPaths();
    if (this.options.layout === 'timeline') {
      this.createAxis();
    }
    this.createLinks();
    this.createNodes();
    this.expandToContent();
    this.createLabelBoxes();
    if (this.options.layout === 'timeline') {
      this.createDistributionHandles();
    }
    this.createNodeLabels();
    this.createLinkLabels();
    this.container
      .style('width', Math.max(this.options.width, this.maxRight))
      .style('height', Math.max(this.options.height, this.maxY));
    return this.container;
  }

  /**
   * Sets the display color of a node.
   *
   * @param targetNode - The node to set the color of.
   * @param color - The color to set.
   */
  public setNodeColor(targetNode: number, color: string) {
    this.timeline.setNodeColor(targetNode, color);
    selectAll<BaseType, TimelineNode>('.nodeFill').attr(
      'fill',
      d => d.layout.color,
    );
  }

  /**
   * Calculates a bezier curve for the given link.
   * @param link - The link to calculate for.
   * @returns The bezier curve.
   */
  private calculateCurve(link: TimelineLink) {
    const y = link.source.layout.y + link.source.layout.height / 2;
    const x = link.source.layout.x + link.source.layout.width;
    const y1 = link.target.layout.y + link.target.layout.height / 2;
    return [
      [x, y],
      [x + this.options.curve.width, y],
      [link.target.layout.x - this.options.curve.width, y1],
      [link.target.layout.x, y1],
    ];
  }

  /**
   * Calculates timeline link paths based on node's X and Y coordinates.
   */
  private calculateLinkPaths() {
    for (const [l, link] of this.graph.links.entries()) {
      let width = this.options.maxLinkWidth;
      if (this.timeline.maxFlow !== 0 && this.options.dynamicLinkWidth) {
        width *= link.flow / this.timeline.maxFlow;
      }
      if (this.graph.links[l]) {
        this.graph.links[l].layout = {
          path: this.getCurvePath(link),
          width,
        };
      }
    }
  }

  /**
   * Given the bounds of the container to render in,
   * construct a clone of the graph with all measurements adjusted to fit in the given range.
   */
  private calculateLayout() {
    const cols: TimelineNode[][] = [];
    const colWidths: number[] = [];
    const colXs: number[] = [];
    const maxCols: number[] = [];
    let maxColumn = -1;
    let maxRow = -1;
    /**
     * Assigns columns along a path.
     *
     * @param source The current source node.
     * @param currentCol The current row number.
     */
    const assignColumns = (source: TimelineNode, currentCol: number) => {
      if (source.layout.column < 0) {
        if (currentCol >= cols.length) {
          cols.push([]);
          maxCols.push(0);
          colWidths.push(0);
          colXs.push(0);
        }
        if (currentCol > maxColumn) {
          maxColumn = currentCol;
        }
        source.setColumn(currentCol);
        cols[currentCol]?.push(source);
        if (source.textWidth > (colWidths[currentCol] ?? 0)) {
          colWidths[currentCol] = source.textWidth;
        }
        for (const link of source.outgoingLinks) {
          assignColumns(link.target, currentCol + 1);
        }
      }
    };
    for (const sourceNode of this.timeline.sourceNodes) {
      assignColumns(sourceNode, 0);
    }
    for (let i = 1; i < colWidths.length; i += 1) {
      colXs[i] = (colXs[i - 1] ?? 0) + (colWidths[i - 1] ?? 0);
    }
    for (const col of cols) {
      for (const [r, element] of col.entries()) {
        element.layout.row = r;
        if (r > maxRow) {
          maxRow = r;
        }
      }
    }
    let currentColor = 0;
    for (const node of this.graph.nodes) {
      if (typeof node.color === 'string') {
        node.setColor(node.color);
      } else {
        node.setColor(colors[currentColor] ?? '');
        currentColor += 1;
        if (currentColor >= colors.length) {
          currentColor = 0;
        }
      }
      if (this.options.layout === 'default') {
        if (node.persist) {
          node.layout.x = node.persist.default.x;
          node.layout.y = node.persist.default.y;
        } else {
          node.layout.x = colXs[node.layout.column] ?? 0;
          node.layout.y
            = (node.layout.row / (maxRow + 1)) * this.options.height;
        }
      } else if (this.options.layout === 'timeline') {
        node.layout.x = this.scale(node.times.meanTime) - node.layout.width / 2;
        node.layout.y = node.persist
          ? node.persist.timeline.y
          : (node.layout.row / (maxRow + 1)) * this.options.height;
        if (hasDist(node.times) && this.options.distributions) {
          node.layout.distribution = [
            {
              x: this.scale(
                node.times.meanTime - (node.times.stdDeviation ?? 0),
              ),
              y: node.layout.y,
            },
            {
              x: this.scale(
                node.times.meanTime + (node.times.stdDeviation ?? 0),
              ),
              y: node.layout.y,
            },
          ];
        }
      }
      if (node.layout.x + node.layout.width > this.maxRight) {
        this.maxRight = node.layout.x + node.layout.width;
      }
      if (node.layout.y + node.layout.height > this.maxY) {
        this.maxY = node.layout.y + node.layout.height;
      }
    }
  }

  /**
   * Places nodes in their initial positions.
   */
  private initializeLayout() {
    for (const [n, node] of this.graph.nodes.entries()) {
      const x = this.scale(node.times.meanTime);
      let height = this.options.maxNodeHeight;
      if (this.options.dynamicNodeHeight) {
        height *= node.size / this.timeline.maxSize;
      }
      if (this.graph.nodes[n]) {
        this.graph.nodes[n].layout = {
          baseRow: 0,
          color: node.color ?? '',
          column: -1,
          height,
          row: -1,
          width: node.layout.width,
          x,
          y: 0,
        };
      }
    }
  }

  /**
   * Calculates the width of the nodes to accomodate the size of their text.
   */
  private calculateLabelSizes() {
    this.container
      .append('g')
      .selectAll('g')
      .data(this.timeline.graph.nodes)
      .join('g')
      .append('text')
      .data(this.timeline.graph.nodes)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('x', '-1000')
      .style('font-size', `${this.options.fontSize.toString()}px`)
      .text(d => d.label)
      .each(function (d) {
        d.textHeight = this.getBBox().height;
        d.textWidth = this.getBBox().width;
        d.layout.width = d.textWidth;
      });
  }

  /**
   * Creates the axis.
   */
  private createAxis() {
    this.container
      .append('g')
      .style('width', '100%')
      .call(axisBottom(this.scale).ticks(this.options.ticks));
  }

  /**
   * Creates links.
   */
  private createLinks() {
    const links = this.container
      .append('g')
      .attr('fill', 'none')
      .selectAll('g')
      .data(this.graph.links)
      .join('g')
      .attr('stroke', d =>
        (color(d.source.layout.color) as RGBColor).toString(),
      )
      .attr('class', 'link')
      .style('mix-blend-mode', 'multiply');

    links
      .append('path')
      .attr('d', d => d.layout.path)
      .attr('stroke-width', d => Math.max(1, d.layout.width));

    links.append('title').text(this.options.linkTitle);
  }

  /**
   * Creates the nodes.
   */
  private createNodes() {
    type TransitionType = Transition<BaseType, null, null, undefined>;
    const { options } = this;
    const _timeline = this.timeline;
    const renderer = this;
    this.container
      .append('g')
      .selectAll('g')
      .data(this.graph.nodes)
      .join('g')
      .attr('x', d => d.layout.x)
      .attr('y', d => d.layout.y)
      .attr('height', d => d.layout.height)
      .attr('width', d => d.layout.width)
      .attr('class', 'node')
      .on('mouseover', function (_event: DragEvent, d) {
        const fade = transition()
          .duration(options.transitionSpeed)
          .ease(easeCubicIn) as TransitionType;
        const target = select(this);
        target.selectAll('.distHandle').transition(fade).style('opacity', 1);
        let shortestPath: number[] = [];
        const paths: number[][] = _timeline.getPath(d.id);
        for (const path of paths) {
          if (shortestPath.length === 0 || path.length < shortestPath.length) {
            shortestPath = path;
          }
        }
        selectAll<BaseType, TimelineNode>('.node').each(function (node) {
          if (!paths.flat().includes(node.id)) {
            select(this).transition(fade).style('opacity', options.fadeOpacity);
          }
        });
        const pathLinks: number[] = [];
        for (const path of paths) {
          pathLinks.push(..._timeline.getLinksInPath(path));
        }
        selectAll<BaseType, TimelineLink>('.link').each(function (link) {
          if (!pathLinks.includes(link.id)) {
            select(this).transition(fade).style('opacity', options.fadeOpacity);
          }
        });
      })
      .on('mouseleave', function () {
        const fade = transition()
          .duration(options.transitionSpeed)
          .ease(easeCubicIn);
        selectAll('.node, .link').transition(fade).style('opacity', 1);
        selectAll('.distHandle')
          .transition(fade)
          .style('opacity', options.fadeOpacity);
      })
      .call(
        drag<any, TimelineNode>()
          .on('drag', (event: DragEvent, d) => {
            d.persist ??= {
              default: {
                x: 0,
                y: 0,
              },
              timeline: {
                y: 0,
              },
            };
            let x = event.x;
            let y = event.y;
            if (x < 0) {
              x = 0;
            }
            if (y < 0) {
              y = 0;
            }
            if (options.layout === 'timeline') {
              d.layout.y = y;
              d.persist.timeline.y = y;
            } else {
              d.layout.x = x;
              d.layout.y = y;
              d.persist.default.x = x;
              d.persist.default.y = y;
            }
            const top = d.layout.y + d.layout.height;
            if (top > this.options.height) {
              this.options.height = top;
              this.container.style('height', top);
            }
            const right = d.layout.x + d.layout.width;
            if (right > this.options.width) {
              this.options.width = right;
              this.container.style('width', right);
            }
          })
          .on('drag.update', () => {
            selectAll<BaseType, TimelineNode>('.node').each(function (d) {
              const element = select<BaseType, TimelineNode>(this);
              element
                .select('.nodeFill')
                .attr('x', () => d.layout.x)
                .attr('y', () => d.layout.y);
              element
                .select('text')
                .attr('x', () => d.layout.x + d.layout.width / 2)
                .attr('y', () => d.layout.y + d.layout.height / 2);
              element
                .select('.meanValue')
                .attr('x', () => d.layout.x + d.layout.width / 2)
                .attr('y', () => d.layout.y - options.meanBar.width);
              element
                .select('.labelBox')
                .attr('x', () => d.layout.x)
                .attr(
                  'y',
                  () => d.layout.y + d.layout.height / 2 - d.textHeight / 2,
                );
              selectAll<BaseType, TimelineLink>('.link').each(function (l) {
                l.layout.path = renderer.getCurvePath(l);
                const current = select(this);
                current.select('path').attr('d', l.layout.path);
                const midpoint = renderer.getCurveMidpoint(l);
                current
                  .selectAll<SVGTextElement, unknown>('text')
                  .attr('x', midpoint?.x ?? 0)
                  .attr('y', function () {
                    let y = midpoint?.y ?? 0;
                    if (l.isSelfLinking) {
                      y
                        = y
                          - renderer.options.curve.height
                          + this.getBBox().height / 2;
                    }
                    return y;
                  });
              });
              element.select('.distHandleLeft').attr('y', () => d.layout.y);
              element
                .select('.distHandleCenter')
                .attr('y', () => d.layout.y + d.layout.height / 2);
              element.select('.distHandleRight').attr('y', () => d.layout.y);
              renderer.emit('positionChanged', d.id, d.layout.x, d.layout.y);
            });
          }),
      );
  }

  /**
   * Ensures the SVG is wide enough that nodes aren't going off the right hand side.
   */
  private expandToContent() {
    for (const node of this.graph.nodes) {
      const right = node.layout.x + node.layout.width;
      if (right > this.options.width) {
        this.options.width = right + this.options.margin;
        this.container.style('width', this.options.width);
      }
    }
    for (const link of this.graph.links) {
      const right = this.getCurveExtrema(link)[1] ?? 0;
      if (right > this.options.width) {
        this.options.width = right + this.options.margin;
        this.container.style('width', this.options.width);
      }
    }
  }

  /**
   * Creates the label boxes.
   */
  private createLabelBoxes() {
    const nodes = selectAll<BaseType, TimelineNode>('.node');
    nodes
      .append('rect')
      .attr('class', 'labelBox')
      .style('fill', 'rgba(0,0,0,0.2)')
      .attr('x', d => d.layout.x)
      .attr('y', d => d.layout.y + d.layout.height / 2 - d.textHeight / 2)
      .attr('width', d => d.textWidth)
      .attr('height', d => d.textHeight);
    nodes
      .append('rect')
      .attr('class', 'nodeFill')
      .attr('fill', d => d.layout.color)
      .attr('x', d => d.layout.x)
      .attr('y', d => d.layout.y)
      .attr('height', d => d.layout.height)
      .attr('width', d => d.layout.width);
    nodes.append('title').text(d => this.options.nodeTitle(d));
  }

  /**
   * Creates distribution handles.
   */
  private createDistributionHandles() {
    const nodes = selectAll<BaseType, TimelineNode>('.node');
    if (this.options.distributions) {
      // Left handle
      nodes
        .append('rect')
        .attr('x', d => d.layout.distribution?.[0]?.x ?? 0)
        .attr('y', d => d.layout.distribution?.[0]?.y ?? 0)
        .attr('class', 'distHandle distHandleLeft')
        .attr('height', d => d.layout.height)
        .attr('width', this.options.distHandle.width)
        .attr('fill', this.options.distHandle.color)
        .style('opacity', this.options.fadeOpacity);
      // Right handle
      nodes
        .append('rect')
        .attr('x', d => d.layout.distribution?.[1]?.x ?? 0)
        .attr('y', d => d.layout.distribution?.[1]?.y ?? 0)
        .attr('class', 'distHandle distHandleRight')
        .attr('height', d => d.layout.height)
        .attr('width', this.options.distHandle.width)
        .attr('fill', this.options.distHandle.color)
        .style('opacity', this.options.fadeOpacity);
      // Center line
      nodes
        .append('rect')
        .attr('x', d => d.layout.distribution?.[0]?.x ?? 0)
        .attr('y', d =>
          d.layout.distribution ? d.layout.y + d.layout.height / 2 : 0,
        )
        .attr('class', 'distHandle distHandleCenter')
        .attr('height', this.options.distHandle.width)
        .attr('width', d =>
          d.layout.distribution
            ? (d.layout.distribution[1]?.x ?? 0)
            - (d.layout.distribution[0]?.x ?? 0)
            : 0,
        )
        .attr('fill', this.options.distHandle.color)
        .style('opacity', this.options.fadeOpacity);
    }

    // Mean value bar
    nodes
      .append('rect')
      .attr('x', d => d.layout.x + d.layout.width / 2)
      .attr('y', d => d.layout.y - this.options.meanBar.width)
      .attr('class', 'meanValue')
      .attr('width', this.options.meanBar.width)
      .attr('height', d => d.layout.height + this.options.meanBar.width * 2)
      .attr('fill', this.options.meanBar.color);
  }

  /**
   * Creates the visible node labels.
   */
  private createNodeLabels() {
    selectAll<BaseType, TimelineNode>('.node')
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('class', 'label')
      .style('fill', this.options.fontColor)
      .style('font-size', `${this.options.fontSize.toString()}px`)
      .text(d => d.label)
      .attr('x', d => d.layout.x + d.layout.width / 2)
      .attr('y', d => d.layout.y + d.layout.height / 2);
  }

  /**
   * Creates link labels.
   */
  private createLinkLabels() {
    const renderer = this;
    selectAll<BaseType, TimelineLink>('.link')
      .append('text')
      .attr('class', 'link-label')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('fill', 'white')
      .style('stroke', 'black')
      .style('stroke-width', this.options.labels.borderWidth)
      .text(d => d.data.count)
      .attr('x', link => this.getCurveMidpoint(link)?.x ?? 0)
      .attr('y', function (link) {
        let y = renderer.getCurveMidpoint(link)?.y ?? 0;
        if (link.isSelfLinking) {
          y = y - renderer.options.curve.height + this.getBBox().height / 2;
        }
        return y;
      });
    selectAll<BaseType, TimelineLink>('.link')
      .append('text')
      .attr('class', 'link-label')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('fill', 'white')
      .style('stroke', 'white')
      .style('stroke-width', this.options.labels.fontSize)
      .text(d => d.data.count)
      .attr('x', link => this.getCurveMidpoint(link)?.x ?? 0)
      .attr('y', function (link) {
        let y = renderer.getCurveMidpoint(link)?.y ?? 0;
        if (link.isSelfLinking) {
          y = y - renderer.options.curve.height + this.getBBox().height / 2;
        }
        return y;
      });
  }

  /**
   * Wraps a curve in the Bezier library.
   * @param link - The link to calculate for.
   * @returns The Bezier curve.
   */
  private getBezierCurve(link: TimelineLink) {
    const curve = this.calculateCurve(link);
    return new Bezier([
      curve[0]?.[0] ?? 0,
      curve[0]?.[1] ?? 0,
      curve[1]?.[0] ?? 0,
      curve[1]?.[1] ?? 0,
      curve[2]?.[0] ?? 0,
      curve[2]?.[1] ?? 0,
      curve[3]?.[0] ?? 0,
      curve[3]?.[1] ?? 0,
    ]);
  }

  /**
   * Calculates the midpoint of a link's bezier curve.
   *
   * @param link - The link to calculate for.
   * @returns The x and y coordinates of the midpoint.
   */
  private getCurveMidpoint(link: TimelineLink) {
    return this.getBezierCurve(link).getLUT()[50];
  }

  /**
   * Gets the minimum and maximum x coordinates of points of the curve.
   * @param link - The link to calculate the curve for.
   * @returns The [minimum, maximum] x coordinates.
   */
  private getCurveExtrema(link: TimelineLink) {
    const curve = this.getBezierCurve(link).getLUT();
    let min = Infinity;
    let max = -Infinity;
    for (const point of curve) {
      if (point.x > max) {
        max = point.x;
      }
      if (point.x < min) {
        min = point.x;
      }
    }
    min -= 28;
    return [min, max];
  }

  /**
   * Calcualtes the SVG path string for a curve.
   * @param link - The link to calculate for.
   * @returns The path string.
   */
  private getCurvePath(link: TimelineLink) {
    const curve = this.calculateCurve(link);
    // TODO: Have curveHeight option effect non-circular curve paths
    // TODO: If the curve width is larger than the nodes it connects to,
    // adjust the curve to be the size of the node at the connection point
    let path = `M${curve[0]?.[0]?.toString() ?? ''},${curve[0]?.[1]?.toString() ?? ''}C${curve[1]?.[0]?.toString() ?? ''},${curve[1]?.[1]?.toString() ?? ''},${curve[2]?.[0]?.toString() ?? ''},${curve[2]?.[1]?.toString() ?? ''},${curve[3]?.[0]?.toString() ?? ''},${curve[3]?.[1]?.toString() ?? ''}`;
    if (link.isSelfLinking) {
      path = `M${(curve[0]?.[0] ?? 0 - 5).toString()},${curve[0]?.[1]?.toString() ?? ''}C${curve[1]?.[0]?.toString() ?? ''},${(
        curve[1]?.[1] ?? 0 - this.options.curve.height
      ).toString()},${curve[2]?.[0]?.toString() ?? ''},${(curve[2]?.[1] ?? 0 - this.options.curve.height).toString()},${(curve[3]?.[0] ?? 0 + 5).toString()},${curve[3]?.[1]?.toString() ?? ''}`;
    }
    return path;
  }
}
